import { activeGraphIconSize } from '../../graph-icon/GraphIcon';
import type { NetworkGraphNodeModel } from '../NetworkGraph';
import type { NetworkGraphPosition } from './computeRadialLayout';

// Gap between a node's outer edge and its label baseline.
const NODE_LABEL_GAP = 6;
// Approximate rendered box of a node label (body-xs), used only to declutter
// overlapping labels — exact metrics are unnecessary for the greedy resolver.
const NODE_LABEL_CHAR_WIDTH = 6.4;
const NODE_LABEL_HEIGHT = 14;

// Active nodes render their label as a 2px-padded title-sm pill. Widths are
// estimated from an average title-sm glyph advance — exact metrics would need a
// getBBox layout pass and aren't worth it for a background rect.
// upgrade to getBBox if labels look clipped
export const SELECTED_LABEL_PADDING = 2;
const SELECTED_LABEL_CHAR_WIDTH = 8;
const SELECTED_LABEL_HEIGHT = 16 + SELECTED_LABEL_PADDING * 2;

const selectedLabelWidth = (label: string) =>
  Math.max(1, label.trim().length) * SELECTED_LABEL_CHAR_WIDTH +
  SELECTED_LABEL_PADDING * 2;

export const nodeDiameter = (size: NetworkGraphNodeModel['size']) =>
  size === 'primary' ? 64 : size === 'active' ? 32 : 24;

export const nodeOuterRadius = (size: NetworkGraphNodeModel['size']) =>
  (size === 'default'
    ? nodeDiameter(size)
    : activeGraphIconSize(nodeDiameter(size))) / 2;

export interface NodeLabelBoxOptions {
  node: NetworkGraphNodeModel;
  position: NetworkGraphPosition;
  connectedPositions?: NetworkGraphPosition[];
  // Placement ray from the layout (degrees, anchor→node): the exact direction
  // the node was fanned out at, recorded when it was placed
  rayAngle?: number;
}

export const nodeLabelBox = ({
  node,
  position,
  connectedPositions = [],
  rayAngle,
}: NodeLabelBoxOptions) => {
  const isActive = node.state === 'active';
  const edgeAngles = connectedPositions
    .map(connected =>
      Math.atan2(connected.y - position.y, connected.x - position.x)
    )
    .toSorted((first, second) => first - second);
  // Default nodes with no layout ray still fan along incident edges.
  const hasRay = rayAngle !== undefined || edgeAngles.length > 0;

  // Active nodes always pin their pill horizontally below the node, ignoring
  // any layout ray: on a ray the selected name renders rotated and reads badly.
  // A single candidate keeps the resolver from shoving it off-center.
  if (isActive) {
    const x = position.x;
    const y =
      position.y +
      nodeOuterRadius(node.size) +
      NODE_LABEL_GAP +
      SELECTED_LABEL_HEIGHT / 2;
    const width = selectedLabelWidth(node.label);

    return {
      id: node.id,
      x,
      y,
      width,
      height: SELECTED_LABEL_HEIGHT,
      angle: 0,
      textWidth: width,
      textHeight: SELECTED_LABEL_HEIGHT,
      anchor: 'middle' as const,
      candidates: [{ x, y }],
    };
  }
  let preferredAngle = Math.PI / 2;

  if (rayAngle !== undefined) {
    preferredAngle = (rayAngle * Math.PI) / 180;
  } else if (edgeAngles.length === 1) {
    preferredAngle = edgeAngles[0] + Math.PI;
  } else if (edgeAngles.length > 1) {
    let largestGap = -1;
    for (let index = 0; index < edgeAngles.length; index += 1) {
      const start = edgeAngles[index];
      const end =
        edgeAngles[(index + 1) % edgeAngles.length] +
        (index === edgeAngles.length - 1 ? Math.PI * 2 : 0);
      const gap = end - start;

      if (gap > largestGap) {
        largestGap = gap;
        preferredAngle = start + gap / 2;
      }
    }
  }

  // Only default nodes reach the ray placement, so the body-xs text box always
  // applies here — the title-sm pill lives on the pinned-below path above.
  const textWidth =
    Math.max(1, node.label.trim().length) * NODE_LABEL_CHAR_WIDTH;
  const textHeight = NODE_LABEL_HEIGHT;
  // The label is rotated to lie along its ray, mirroring edgeLabelBox's
  // normalization to (-90°, 90°] so text never renders upside down. A ring of
  // labels then continues each arrow's ray (a starburst) exactly like the
  // relationship labels do, instead of horizontal boxes that clip neighbors.
  // Nodes with no ray and no edges keep a horizontal label.
  const rotation = hasRay
    ? ((((preferredAngle * 180) / Math.PI + 450) % 180) - 90) * (Math.PI / 180)
    : 0;
  // Place the label along the preferred direction itself (for a fanned leaf that
  // is the arrow's outward direction, so the label continues the arrow rather
  // than snapping to a cardinal and landing off to the side). Every collision
  // fallback stays on that geometry: it slides further out along the ray or
  // shifts one label-height sideways, PARALLEL to the text. The old angular
  // fallbacks rotated the position without rotating the text, so a bumped
  // label floated detached from both its node and its arrow.
  const radius = nodeOuterRadius(node.size);
  const slideStep = NODE_LABEL_HEIGHT + NODE_LABEL_GAP;
  const cos = Math.cos(preferredAngle);
  const sin = Math.sin(preferredAngle);
  // Clearance of the ROTATED label box along the ray: with the text lying on
  // the ray itself (delta 0) that is half the text length. hypot clearance is
  // tighter than |cos|·w/2+|sin|·h/2 on diagonals.
  const delta = preferredAngle - rotation;
  const extent = Math.hypot(
    Math.cos(delta) * (textWidth / 2),
    Math.sin(delta) * (textHeight / 2)
  );
  const candidates = [
    { slide: 0, lane: 0 },
    { slide: 1, lane: 0 },
    { slide: 2, lane: 0 },
    { slide: 0, lane: 1 },
    { slide: 0, lane: -1 },
    { slide: 1, lane: 1 },
    { slide: 1, lane: -1 },
    { slide: 3, lane: 0 },
    { slide: 4, lane: 0 },
  ].map(({ slide, lane }) => {
    const base = radius + NODE_LABEL_GAP + extent + slide * slideStep;
    const lateral = lane * slideStep;

    return {
      x: position.x + cos * base - sin * lateral,
      y: position.y + sin * base + cos * lateral,
    };
  });
  const [preferred] = candidates;
  // Collision solver works with axis-aligned boxes, so expose the rotated
  // label's bounding box there while the renderer keeps the raw text metrics.
  const bboxCos = Math.abs(Math.cos(rotation));
  const bboxSin = Math.abs(Math.sin(rotation));
  // The label center is placed at radius+gap+textWidth/2, but textWidth is only
  // an estimate (charCount * avg advance).
  // Delta is a multiple of 180°, so cos(delta) is ±1: >0 puts
  // the node on the label's left (pin the start edge), <0 on the right (pin the
  // end). Rayless/isolated labels stay centered below their node.
  const anchor: 'start' | 'middle' | 'end' = hasRay
    ? Math.cos(delta) > 0
      ? 'start'
      : 'end'
    : 'middle';

  return {
    id: node.id,
    x: preferred.x,
    y: preferred.y,
    width: bboxCos * textWidth + bboxSin * textHeight,
    height: bboxSin * textWidth + bboxCos * textHeight,
    angle: (rotation * 180) / Math.PI,
    textWidth,
    textHeight,
    anchor,
    candidates,
  };
};
